// NotificationContext.tsx
import { createContext, useContext, useEffect, useState } from "react";
import { IconComponent, IconsTypes } from "@ui/icon";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import toast from "react-hot-toast";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/16/solid";
import { Format } from "@/utils/formatters";
import { displayWalletAddress } from "@/utils/wallet";
import { BotBalance, Trend, Value } from "@/ui/formats";

type MessageType = "error" | "success" | "none";

type ActionType =
  | "buy"
  | "sell"
  | "placeLimitBuy"
  | "placeLimitSell"
  | "sendToken"
  | "limitBuy"
  | "limitSell"
  | "takeProfit"
  | "stopLoss"
  | "copyBuy"
  | "copySell";

export type NotificationParams = {
  youSell?: string;
  tokenSymbol?: string;
  chainSymbol?: string;
  youReceiveUSD?: string;
  youReceive?: string;
  youPayNative?: string;
  youPayUSD?: string;
  limitPrice?: string;
  amount?: string;
  walletAddress?: string;
  pnl?: number;
  roi?: number;
  currentBalance?: number;
  duration?: string;
  link?: string;
};

type MessageTemplate = (params: NotificationParams) => JSX.Element;

type MessageTemplates = {
  [K in Exclude<MessageType, "none">]: Record<ActionType, MessageTemplate>;
} & {
  none: () => JSX.Element;
};

const resolveIcon = (type: MessageType) => {
  switch (type) {
    case "success":
      return (
        <IconComponent name="xCheck" className="w-[0.625rem] h-[0.625rem]" />
      );
    case "error":
      return <IconComponent name="xError" className="w-[0.5rem] h-[0.5rem]" />;
    default:
      return null;
  }
};

const NotificationItemContainer = ({
  type,
  title,
  description,
  link,
  icon,
  expandable,
}: {
  type: MessageType;
  title: string;
  description: string;
  link?: string;
  icon?: IconsTypes;
  expandable?: React.ReactNode;
}) => {
  const [expanded, setExpanded] = useState(false);

  const mainSection = () => {
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-x-[0.313rem]">
          {resolveIcon(type)}
          <span className="text-xs font-semibold">{title}</span>
          {link && icon && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <IconComponent
                name={icon}
                className="h-[0.625rem] w-[0.625rem] bg-opacity-50 hover:bg-opacity-100"
              />
            </a>
          )}
        </div>
        <span className="text-xs">{description}</span>
      </div>
    );
  };

  if (expandable) {
    return (
      <div className="flex items-center">
        <div className="flex flex-col gap-y-[0.313rem]">
          {mainSection()}
          {expanded && expandable}
        </div>
        <div
          className="p-[0.625rem] pr-[0.313rem] cursor-pointer"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? (
            <ChevronUpIcon className="h-[1rem] w-[1rem]" />
          ) : (
            <ChevronDownIcon className="h-[1rem] w-[1rem]" />
          )}
        </div>
      </div>
    );
  }

  return mainSection();
};

export const messageTemplates: MessageTemplates = {
  success: {
    buy: ({ youReceive, tokenSymbol, youPayNative, chainSymbol }) => (
      <NotificationItemContainer
        type="success"
        title="Instant Buy - Success!"
        description={`Just bought ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol}`}
      />
    ),
    sell: ({
      youSell,
      tokenSymbol,
      youReceive,
      chainSymbol,
      pnl,
      roi,
      duration,
      currentBalance,
    }) => (
      <NotificationItemContainer
        type="success"
        title="Instant Sell - Success!"
        description={`Just sold ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} ${youReceive ? "for" : ""} ${
          youReceive ? Format.from(Number(youReceive)).unit().toString() : ""
        } ${youReceive ? chainSymbol : ""}`}
        expandable={
          <div className="flex flex-col">
            <div className="flex items-center gap-x-[0.313rem]">
              <span className="text-xs flex items-center">
                PNL:
                <div className="pl-[0.188rem]">
                  <Value
                    value={pnl}
                    withSign={false}
                    positiveNegativeColor
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
              <span className="text-xs flex items-center">
                ROI:
                <div className="pl-[0.188rem]">
                  <Trend
                    value={roi ?? 0}
                    withSign={false}
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
            </div>
            <span className="text-xs ">{`Duration: ${duration}`}</span>
            <div className="flex items-center">
              <span className="text-xs flex items-center">
                Current Balance:
                <div className="px-[0.188rem]">
                  <BotBalance value={currentBalance ?? 0} />
                </div>
                {tokenSymbol}
              </span>
            </div>
          </div>
        }
      />
    ),
    placeLimitBuy: ({
      youReceive,
      tokenSymbol,
      youPayNative,
      chainSymbol,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="success"
        title="Place Limit Buy - Success!"
        description={`Buy ${Format.from(youReceive ? Number(youReceive) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    placeLimitSell: ({
      youSell,
      tokenSymbol,
      youPayNative,
      chainSymbol,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="success"
        title="Place Limit Sell - Success!"
        description={`Sell ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    sendToken: ({ amount, tokenSymbol, walletAddress }) => (
      <NotificationItemContainer
        type="success"
        title="Send - Success!"
        description={`Just sent ${Format.from(amount ? Number(amount) : 0)
          .unit()
          .toString()} ${tokenSymbol} to ${displayWalletAddress(walletAddress ?? "")}`}
      />
    ),
    limitBuy: ({
      youReceive,
      tokenSymbol,
      youPayNative,
      chainSymbol,
      link,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="success"
        title="Limit Buy - Success!"
        icon="barChart"
        link={link}
        description={`Just bought ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    limitSell: ({
      youSell,
      tokenSymbol,
      youReceive,
      chainSymbol,
      pnl,
      roi,
      duration,
      currentBalance,
      link,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="success"
        title="Limit Sell - Success!"
        icon="barChart"
        link={link}
        description={`Just sold ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
        expandable={
          <div className="flex flex-col">
            <div className="flex items-center gap-x-[0.313rem]">
              <span className="text-xs flex items-center">
                PNL:
                <div className="pl-[0.188rem]">
                  <Value
                    value={pnl}
                    withSign={false}
                    positiveNegativeColor
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
              <span className="text-xs flex items-center">
                ROI:
                <div className="pl-[0.188rem]">
                  <Trend
                    value={roi ?? 0}
                    withSign={false}
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
            </div>
            <span className="text-xs ">{`Duration: ${duration}`}</span>
            <div className="flex items-center">
              <span className="text-xs flex items-center">
                Current Balance:
                <div className="px-[0.188rem]">
                  <BotBalance value={currentBalance ?? 0} />
                </div>
                {tokenSymbol}
              </span>
            </div>
          </div>
        }
      />
    ),
    takeProfit: ({
      youSell,
      tokenSymbol,
      youReceive,
      chainSymbol,
      pnl,
      roi,
      duration,
      currentBalance,
      link,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="success"
        title="Take Profit - Success!"
        icon="barChart"
        link={link}
        description={`Just sold ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
        expandable={
          <div className="flex flex-col">
            <div className="flex items-center gap-x-[0.313rem]">
              <span className="text-xs flex items-center">
                PNL:
                <div className="pl-[0.188rem]">
                  <Value
                    value={pnl}
                    withSign={false}
                    positiveNegativeColor
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
              <span className="text-xs flex items-center">
                ROI:
                <div className="pl-[0.188rem]">
                  <Trend
                    value={roi ?? 0}
                    withSign={false}
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
            </div>
            <span className="text-xs ">{`Duration: ${duration}`}</span>
            <div className="flex items-center">
              <span className="text-xs flex items-center">
                Current Balance:
                <div className="px-[0.188rem]">
                  <BotBalance value={currentBalance ?? 0} />
                </div>
                {tokenSymbol}
              </span>
            </div>
          </div>
        }
      />
    ),
    stopLoss: ({
      youSell,
      tokenSymbol,
      youReceive,
      chainSymbol,
      pnl,
      roi,
      duration,
      currentBalance,
      link,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="success"
        title="Stop Loss - Success!"
        icon="barChart"
        link={link}
        description={`Just sold ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
        expandable={
          <div className="flex flex-col">
            <div className="flex items-center gap-x-[0.313rem]">
              <span className="text-xs flex items-center">
                PNL:
                <div className="pl-[0.188rem]">
                  <Value
                    value={pnl}
                    withSign={false}
                    positiveNegativeColor
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
              <span className="text-xs flex items-center">
                ROI:
                <div className="pl-[0.188rem]">
                  <Trend
                    value={roi ?? 0}
                    withSign={false}
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
            </div>
            <span className="text-xs ">{`Duration: ${duration}`}</span>
            <div className="flex items-center">
              <span className="text-xs flex items-center">
                Current Balance:
                <div className="px-[0.188rem]">
                  <BotBalance value={currentBalance ?? 0} />
                </div>
                {tokenSymbol}
              </span>
            </div>
          </div>
        }
      />
    ),
    copyBuy: ({
      youReceive,
      tokenSymbol,
      youPayNative,
      chainSymbol,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="success"
        title="Copy Buy - Success!"
        description={`Just bought ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    copySell: ({
      youSell,
      tokenSymbol,
      youReceive,
      chainSymbol,
      pnl,
      roi,
      duration,
      currentBalance,
      link,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="success"
        title="Copy Sell - Success!"
        icon="barChart"
        link={link}
        description={`Just sold ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
        expandable={
          <div className="flex flex-col">
            <div className="flex items-center gap-x-[0.313rem]">
              <span className="text-xs flex items-center">
                PNL:
                <div className="pl-[0.188rem]">
                  <Value
                    value={pnl}
                    withSign={false}
                    positiveNegativeColor
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
              <span className="text-xs flex items-center">
                ROI:
                <div className="pl-[0.188rem]">
                  <Trend
                    value={roi ?? 0}
                    withSign={false}
                    withTrendArrow
                    smallSpace
                  />
                </div>
              </span>
            </div>
            <span className="text-xs ">{`Duration: ${duration}`}</span>
            <div className="flex items-center">
              <span className="text-xs flex items-center">
                Current Balance:
                <div className="px-[0.188rem]">
                  <BotBalance value={currentBalance ?? 0} />
                </div>
                {tokenSymbol}
              </span>
            </div>
          </div>
        }
      />
    ),
  },
  error: {
    buy: ({ youReceive, tokenSymbol, youPayNative, chainSymbol }) => (
      <NotificationItemContainer
        type="error"
        title="Instant Buy - Failed!"
        description={`Buy ${Format.from(youReceive ? Number(youReceive) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol}`}
      />
    ),
    sell: ({ youSell, tokenSymbol, youReceive, chainSymbol }) => (
      <NotificationItemContainer
        type="error"
        title="Instant Sell - Failed!"
        description={`Sell ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${chainSymbol}`}
      />
    ),
    placeLimitBuy: ({
      youReceive,
      tokenSymbol,
      youPayNative,
      chainSymbol,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="error"
        title="Place Limit Buy - Failed!"
        description={`Buy ${Format.from(youReceive ? Number(youReceive) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    placeLimitSell: ({
      youSell,
      tokenSymbol,
      youPayNative,
      chainSymbol,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="error"
        title="Place Limit Sell - Failed!"
        description={`Sell ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    sendToken: ({ amount, tokenSymbol, walletAddress }) => (
      <NotificationItemContainer
        type="error"
        title="Send - Failed!"
        description={`Send ${Format.from(amount ? Number(amount) : 0)
          .unit()
          .toString()} ${tokenSymbol} to ${displayWalletAddress(walletAddress ?? "")}`}
      />
    ),
    limitBuy: ({
      youReceive,
      tokenSymbol,
      youPayNative,
      chainSymbol,
      link,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="error"
        title="Limit Buy - Failed!"
        icon="barChart"
        link={link}
        description={`Buy ${Format.from(youReceive ? Number(youReceive) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    limitSell: ({
      youSell,
      tokenSymbol,
      youReceive,
      chainSymbol,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="error"
        title="Limit Sell - Failed!"
        description={`Sell ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    takeProfit: ({
      youSell,
      tokenSymbol,
      youReceive,
      chainSymbol,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="error"
        title="Take Profit - Failed!"
        description={`Sell ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    stopLoss: ({
      youSell,
      tokenSymbol,
      youReceive,
      chainSymbol,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="error"
        title="Stop Loss - Failed!"
        description={`Sell ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
    copyBuy: ({ youReceive, tokenSymbol, youPayNative, chainSymbol }) => (
      <NotificationItemContainer
        type="error"
        title="Copy Buy - Failed!"
        description={`Buy ${Format.from(youReceive ? Number(youReceive) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youPayNative ? Number(youPayNative) : 0
        )
          .unit()
          .toString()} ${chainSymbol}`}
      />
    ),
    copySell: ({
      youSell,
      tokenSymbol,
      youReceive,
      chainSymbol,
      limitPrice,
    }) => (
      <NotificationItemContainer
        type="error"
        title="Copy Sell - Failed!"
        description={`Sell ${Format.from(youSell ? Number(youSell) : 0)
          .unit()
          .toString()} ${tokenSymbol} for ${Format.from(
          youReceive ? Number(youReceive) : 0
        )
          .unit()
          .toString()} ${chainSymbol} at ${Format.from(
          limitPrice ? Number(limitPrice) : 0
        )
          .unit()
          .currency()
          .toString()}`}
      />
    ),
  },
  none: () => <>No new notifications</>,
};

interface NotificationProps {
  type: MessageType;
  action?: ActionType;
  params?: NotificationParams;
}

export const NotificationMessage = ({
  type,
  action,
  params = {},
}: NotificationProps) => {
  if (type === "none") return <>{messageTemplates.none()}</>;

  const template = messageTemplates[type]?.[action!];

  if (!template) return null;

  return <>{template(params as NotificationParams)}</>;
};

type NotificationContextType = {
  notifyProps: NotificationProps;
  setNotifyProps: (msg: NotificationProps) => void;
};

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [notifyProps, setNotifyProps] = useState<NotificationProps>({
    type: "none",
  });

  useEffect(() => {
    if (notifyProps.type === "none") return;

    toast.custom(
      (t) => (
        <div className={t.visible ? "animate-enter" : "animate-leave"}>
          <GradientBorderRadius radius="normal" gradient="reversed-100">
            <div className="bg-black shrink-0 text-white text-xs px-[0.625rem] py-[0.313rem]">
              <NotificationMessage {...notifyProps} />
            </div>
          </GradientBorderRadius>
        </div>
      ),
      {
        position: "top-right",
        duration: 3000,
      }
    );
  }, [notifyProps]);

  return (
    <NotificationContext.Provider value={{ notifyProps, setNotifyProps }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};
